// Mesure d'audience Matomo — option A (décision de William, 25/10/2026), dans le cadre de l'exemption CNIL :
// - visiteur sans choix : mesure anonyme SANS cookie (requireCookieConsent ; adresse IP tronquée côté serveur Matomo) ;
// - « Accepter » : cookies de mesure autorisés (setCookieConsentGiven, rejoué à chaque chargement depuis notre stockage) ;
// - « Refuser » : opposition, plus rien n'est envoyé. Matomo n'est pas chargé ; s'il l'était déjà dans la page,
//   requireConsent bloque les requêtes suivantes, forgetCookieConsentGiven et l'effacement retirent ses cookies.
// UN seul mécanisme : aucun extrait Matomo dans public/index.html, et rien pendant le pré-rendu (react-snap figeait
// la balise <script> de matomo.js dans le HTML, ce qui cassait l'initialisation du suivi).
import { COOKIE_CONSENT_ACCEPTED, COOKIE_CONSENT_REJECTED } from './cookieConsent';

const MATOMO_BASE_URL = 'https://piwik.meteris.fr/';
const MATOMO_SITE_ID = '19';
const MATOMO_SCRIPT_ID = 'meteris-matomo-script';

let matomoPromise = null;
let matomoConfigure = false;
let oppose = false;

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

// react-snap se présente comme « ReactSnap » : pendant le pré-rendu, aucune balise ni requête Matomo.
export function estPreRendu() {
  return typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent || '');
}

function getMatomoQueue() {
  window._paq = window._paq || [];
  return window._paq;
}

function configurer(cookiesAcceptes) {
  if (matomoConfigure) {
    return;
  }
  const _paq = getMatomoQueue();
  _paq.push(['requireCookieConsent']);
  if (cookiesAcceptes) {
    _paq.push(['setCookieConsentGiven']);
  }
  _paq.push(['setTrackerUrl', `${MATOMO_BASE_URL}matomo.php`]);
  _paq.push(['setSiteId', MATOMO_SITE_ID]);
  _paq.push(['enableLinkTracking']);
  matomoConfigure = true;
}

/**
 * Charge Matomo (une seule fois). `cookiesAcceptes` : le visiteur a accepté les cookies de mesure.
 * Rend une promesse résolue à false quand rien n'est chargé (refus, pré-rendu, hors navigateur).
 */
export function chargerMatomo(cookiesAcceptes) {
  if (!isBrowser() || estPreRendu() || oppose) {
    return Promise.resolve(false);
  }

  configurer(cookiesAcceptes);

  if (matomoPromise) {
    return matomoPromise;
  }

  if (document.getElementById(MATOMO_SCRIPT_ID)) {
    matomoPromise = Promise.resolve(true);
    return matomoPromise;
  }

  matomoPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = MATOMO_SCRIPT_ID;
    script.async = true;
    script.src = `${MATOMO_BASE_URL}matomo.js`;
    script.onload = () => resolve(true);
    script.onerror = () => {
      matomoPromise = null;
      reject(new Error('Matomo indisponible.'));
    };
    document.head.appendChild(script);
  });

  return matomoPromise;
}

/**
 * Applique le choix du visiteur : null (pas encore de choix), accepté ou refusé.
 * Appelé au démarrage avec le choix mémorisé, puis à chaque changement.
 */
export function appliquerChoix(choix) {
  if (!isBrowser()) {
    return;
  }

  if (choix === COOKIE_CONSENT_REJECTED) {
    oppose = true;
    effacerCookiesMatomo();
    if (matomoConfigure) {
      const _paq = getMatomoQueue();
      _paq.push(['requireConsent']);
      _paq.push(['forgetCookieConsentGiven']);
    }
    return;
  }

  const etaitOppose = oppose;
  oppose = false;

  if (matomoConfigure) {
    const _paq = getMatomoQueue();
    if (etaitOppose) {
      _paq.push(['setConsentGiven']);
    }
    if (choix === COOKIE_CONSENT_ACCEPTED) {
      _paq.push(['setCookieConsentGiven']);
    }
  }
}

export function trackMatomoPageView(path) {
  if (!isBrowser() || oppose || !window._paq) {
    return;
  }

  const customUrl = new URL(path || window.location.pathname, window.location.origin).toString();

  window._paq.push(['setCustomUrl', customUrl]);

  if (document.title) {
    window._paq.push(['setDocumentTitle', document.title]);
  }

  window._paq.push(['trackPageView']);
}

export function trackMatomoEvent(category, action, name, value) {
  if (!isBrowser() || oppose || !window._paq) {
    return;
  }
  const event = ['trackEvent', category, action];
  if (name !== undefined) event.push(name);
  if (value !== undefined) event.push(value);
  window._paq.push(event);
}

function effacerCookiesMatomo() {
  const cookieNames = document.cookie
    .split(';')
    .map((cookie) => cookie.trim().split('=')[0])
    .filter((name) => name.startsWith('_pk_') || name.startsWith('mtm_'));

  if (cookieNames.length === 0) {
    return;
  }

  const domains = Array.from(new Set([
    window.location.hostname,
    `.${window.location.hostname}`,
    '.meteris.fr',
  ]));

  cookieNames.forEach((name) => {
    expireCookie(name, '/');
    domains.forEach((domain) => {
      expireCookie(name, '/', domain);
    });
  });
}

function expireCookie(name, path, domain) {
  const domainPart = domain ? ` domain=${domain};` : '';
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path};${domainPart}`;
}
